/**
 * src/components/shared/workbenchLayout.js
 *
 * Column width fitting for the shared Workbench (src/components/shared/Workbench.jsx).
 *
 * The Workbench renders with `table-layout: fixed` and an explicit <colgroup>,
 * so the browser never derives column widths from cell content. That is
 * deliberate: under the browser's automatic table layout every filterable
 * column inherited a ~180px floor from the intrinsic width of the <input> /
 * <select> in its filter row, the table blew past its container, and the
 * rightmost column (usually the row actions) ended up behind a horizontal
 * scrollbar, off screen.
 *
 * This module owns the arithmetic that replaces that: given each visible
 * column's desired / minimum / maximum width and the measured container
 * width, produce a width per column whose total fits the container.
 *
 * Kept free of React so the distribution rules can be unit-tested directly.
 */

/** Width used for a column whose consumer declared no `size`. */
export const DEFAULT_COL_WIDTH = 240;
/** Floor for a column that declares no `minSize`. */
export const MIN_COL_WIDTH = 60;
/**
 * Ceiling for a hand-drag resize. Deliberately *not* applied to the automatic
 * fit: a column left unbounded is free to absorb all the surplus on a wide
 * screen, which is what keeps the table flush with its container instead of
 * ending in a ragged gap. Only a `maxSize` the consumer declares caps growth.
 */
export const MAX_COL_WIDTH = 800;

const EPSILON = 0.5;
const MAX_PASSES = 8;

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

function normalise(spec) {
  const min = Math.max(1, Number.isFinite(spec.min) ? spec.min : MIN_COL_WIDTH);
  const max = Math.max(min, Number.isFinite(spec.max) ? spec.max : Infinity);
  const desired = Number.isFinite(spec.desired) ? spec.desired : DEFAULT_COL_WIDTH;
  return {
    id: spec.id,
    min,
    max,
    // Flexible columns absorb surplus space and give it back first when the
    // container is too narrow. A column the consumer sized explicitly, or the
    // user resized by hand, is not flexible — it keeps its width until the
    // flexible ones have nothing left to give.
    flexible: !!spec.flexible,
    w: clamp(desired, min, max),
  };
}

function totalWidth(cols) {
  return cols.reduce((sum, c) => sum + c.w, 0);
}

/** Hand `surplus` px out to the flexible columns, in proportion to their width. */
function grow(cols, surplus) {
  let remaining = surplus;
  for (let pass = 0; pass < MAX_PASSES && remaining > EPSILON; pass++) {
    const pool = cols.filter((c) => c.flexible && c.w < c.max);
    if (!pool.length) return;
    const weight = pool.reduce((sum, c) => sum + c.w, 0) || pool.length;
    let used = 0;
    for (const c of pool) {
      const next = Math.min(c.max, c.w + remaining * ((c.w || 1) / weight));
      used += next - c.w;
      c.w = next;
    }
    if (used <= EPSILON) return;
    remaining -= used;
  }
}

/**
 * Reclaim `deficit` px. Flexible columns are squeezed first, in proportion to
 * how much slack each has above its own minimum; only once they are all at
 * their minimum do explicitly-sized columns start to give.
 */
function shrink(cols, deficit) {
  let remaining = deficit;
  const groups = [cols.filter((c) => c.flexible), cols.filter((c) => !c.flexible)];
  for (const group of groups) {
    for (let pass = 0; pass < MAX_PASSES && remaining > EPSILON; pass++) {
      const pool = group.filter((c) => c.w > c.min);
      if (!pool.length) break;
      const slack = pool.reduce((sum, c) => sum + (c.w - c.min), 0);
      if (slack <= EPSILON) break;
      let freed = 0;
      for (const c of pool) {
        const next = Math.max(c.min, c.w - remaining * ((c.w - c.min) / slack));
        freed += c.w - next;
        c.w = next;
      }
      if (freed <= EPSILON) break;
      remaining -= freed;
    }
    if (remaining <= EPSILON) return;
  }
}

/**
 * Fit a set of columns to the available width.
 *
 * @param {Array<{id: string, desired?: number, min?: number, max?: number, flexible?: boolean}>} specs
 *   One entry per visible column, in render order.
 * @param {number} available
 *   Measured content width of the scroll container, in px. Pass 0 (or omit)
 *   before the container has been measured — the desired widths are then
 *   returned unchanged.
 * @returns {Record<string, number>} column id → width in px.
 *
 * The returned widths sum to `available` whenever that is achievable. When the
 * columns' minimums add up to more than the container can hold — many columns
 * on a narrow screen — the total exceeds `available` and the caller falls back
 * to horizontal scrolling, which at that point is unavoidable.
 */
export function computeFittedColumnWidths(specs, available) {
  const cols = (specs || []).map(normalise);
  if (!cols.length) return {};

  const avail = Number.isFinite(available) ? Math.floor(available) : 0;
  if (avail > 0) {
    const diff = avail - totalWidth(cols);
    if (diff > EPSILON) grow(cols, diff);
    else if (diff < -EPSILON) shrink(cols, -diff);
  }

  // Largest-remainder rounding: rounding each column independently would leave
  // the total a pixel or two short of the container and put a hairline gap at
  // the right-hand edge of the table.
  const widths = cols.map((c) => Math.max(1, Math.floor(c.w)));
  let leftover = Math.round(totalWidth(cols)) - widths.reduce((sum, w) => sum + w, 0);
  const byFraction = cols
    .map((c, i) => ({ i, fraction: c.w - Math.floor(c.w) }))
    .sort((a, b) => b.fraction - a.fraction);
  for (let k = 0; leftover > 0 && k < byFraction.length; k++, leftover--) {
    widths[byFraction[k].i] += 1;
  }

  const out = {};
  cols.forEach((c, i) => {
    out[c.id] = widths[i];
  });
  return out;
}
